/**
 * Hook that returns the correct brand logo/icon for the current theme.
 * Re-renders when the active brand changes (super-admin tenant switching).
 */

import { useTheme } from '@/hooks/useTheme';
import { useBrand } from './useBrand';

export function useBrandAssets() {
  const { isDark } = useTheme();
  const brand = useBrand();

  const icon = isDark && brand.iconDark ? brand.iconDark : brand.icon;
  const logo = isDark && brand.logoDark ? brand.logoDark : brand.logo;

  /**
   * A special mark (concierge, anniversary) follows the regular asset it
   * stands in for. In dark mode it is used only if it is known to survive
   * there: its own dark twin when one is set; otherwise, if the brand needed a
   * dark twin for the REGULAR asset (`iconDark` / `logoDark`), its light-only
   * special mark would not survive either — so the regular dark asset shows
   * and the treatment is simply absent, never a navy mark on a navy rail. A
   * brand whose regular asset serves both themes (no dark twin, e.g. a logo
   * that always sits on a light panel) has special marks that do too.
   */
  const special = (
    light: string | undefined,
    dark: string | undefined,
    regularLight: string,
    regularDark: string | undefined,
  ): string => {
    if (!isDark) return light || regularLight;
    if (dark) return dark;
    if (regularDark) return regularDark;
    return light || regularLight;
  };

  return {
    logo,
    icon,
    conciergeIcon: special(brand.conciergeIcon, brand.conciergeIconDark, brand.icon, brand.iconDark),
    // Temporary 10th-anniversary treatment — falls back to the normal
    // icon/logo everywhere it isn't explicitly set (i.e. every brand but m2,
    // and every m2 spot except the sidebar mark + login logo).
    anniversaryIcon: special(brand.anniversaryIcon, brand.anniversaryIconDark, brand.icon, brand.iconDark),
    anniversaryLogo: special(brand.anniversaryLogo, brand.anniversaryLogoDark, brand.logo, brand.logoDark),
  };
}
